import {
  G_PROXY_BASE_URL,
  IS_DEBUG,
} from '@/core/constants/appConfig/environment';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { EDestinationType } from '@/core/interfaces';
import { GA4Wrapper } from '@/destinations/shared/gtag/tag_manager_wrapper/ga4_wrapper';
import { TGtagUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/types';
import { processGoogleUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/user_data_helper';
import { DestinationStatusHandler } from '@/destinations/status';
import { TGA4DestinationCredentials } from '@/destinations/types/destination_creds_types';
import { Logger } from '@/shared/logger/logger';

import {
  DestinationBundle,
  DestinationPayload,
  DestinationSendingResult,
} from '../abstract';
import { Ga4CookieLogic } from './cookie_logic';
import { transformMableEventToGA4Event } from './event_transformer';
import { GA4Event } from './types';

export class GA4DestinationBundle extends DestinationBundle {
  override allowedEvents: EMableEventName[] = [
    EMableEventName.PAGE_VIEWED,
    EMableEventName.PRODUCT_ADDED,
    EMableEventName.CHECKOUT_STARTED,
    EMableEventName.PAYMENT_INFO_ADDED,
    EMableEventName.PRODUCT_VIEWED,
    EMableEventName.LEAD,
    EMableEventName.CUSTOM_EVENT,
    // EMableEventName.COLLECTION_VIEWED,
    EMableEventName.ORDER_COMPLETED,
  ];

  override type: EDestinationType = EDestinationType.GA4;
  private readonly ga4: GA4Wrapper;
  private readonly gaMeasurementId: string;
  private gtagStatus!: any;
  private customDomainStatus!: any;
  private sgtmDomainStatus!: any;
  private readonly sgtmContainerUrl: string | undefined;
  override hasBrowserDestination = true;

  constructor(
    destinationSetting: TGA4DestinationCredentials,
    destinationId: string,
  ) {
    super(EDestinationType.GA4, destinationSetting, destinationId);

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (
      !destinationSetting?.measurementId &&
      !destinationSetting['config']?.measurementId
    ) {
      throw new Error(
        'GA4DestinationBundle: Missing required configuration parameter: measurementId for GA4 destination',
      );
    }

    this.ga4 = new GA4Wrapper();
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    this.gaMeasurementId =
      destinationSetting.measurementId ||
      destinationSetting['config']?.measurementId;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    this.sgtmContainerUrl =
      destinationSetting['sgtm_container_url'] ||
      destinationSetting['config']?.sgtm_container_url;

    this.cookiesToUpdate.push(
      Ga4CookieLogic.getGaCidCookieKey(this.gaMeasurementId),
    );

    this.gtagStatus = new DestinationStatusHandler({
      destinationUrl: 'https://www.googletagmanager.com/gtag/js',
      storageNamespace: 'ga4(Status)',
    });

    // https://region1.google-analytics.com/g/collect?v=2&tid=G-BGP6D0TXX7&gtm=45je4ah0v898111481za200&_p=1729688559370&gcs=G111&gcd=13v3v3v2v5l1&npa=0&dma_cps=syphamo&dma=1&tag_exp=101533421~101686685~101823848&cid=802187048.1729592130&ul=en-gb&sr=5120x2160&uaa=arm&uab=64&uafvl=Chromium%3B130.0.0.0%7CBrave%3B130.0.0.0%7CNot%253FA_Brand%3B99.0.0.0&uamb=0&uam=&uap=macOS&uapv=15.0.1&uaw=0&frm=1&pscdl=noapi&_eu=AEA&_s=2&sid=1729688559&sct=3&seg=0&dl=https%3A%2F%2Fwtag-dev-store.myshopify.com%2Fwpm%4010e09e41w77fb031apd6c28ceam532d5d9a%2Fcustom%2Fweb-pixel-117735765%4029%2Fsandbox%2Fmodern%2F&dt=wtag-dev-store&uid=0192b3e7-b8db-7bbd-aeb2-6ab6a3e2e14f&en=scroll&_ss=1&epn.percent_scrolled=90&tfd=1404

    this.customDomainStatus = new DestinationStatusHandler({
      destinationUrl: `${G_PROXY_BASE_URL}/wtag/js`,
      storageNamespace: 'ga4CustomDomain(Status)',
    });

    //TODO: replace with wtag for add blockers later
    this.sgtmDomainStatus = new DestinationStatusHandler({
      destinationUrl: `${this.sgtmContainerUrl}/gtag/js?id=${this.gaMeasurementId}`,
      storageNamespace: 'ga4SgtmDomain(Status)',
    });
  }

  override getBrowserDestinationSendingStatus(): boolean {
    return this.sgtmContainerUrl
      ? !!this.sgtmDomainStatus.isAvailable
      : !!(this.gtagStatus.isAvailable || this.customDomainStatus.isAvailable);
  }

  override async init(): Promise<void> {
    try {
      // Initialize status checkers
      this.gtagStatus = await this.gtagStatus.getStatus();
      this.customDomainStatus = await this.customDomainStatus.getStatus();
      this.sgtmDomainStatus = await this.sgtmDomainStatus.getStatus();

      // Detect if an ad blocker is active
      if (this.gtagStatus.isAvailable) {
        Logger.warn('Ad Blocker Not Detected');
      } else {
        Logger.warn(
          'GA4DestinationBundle: Using proxy for GA4 due to ad blocker',
        );
      }

      const useProxy = !this.gtagStatus.isAvailable;
      const baseUrl =
        this.sgtmContainerUrl || (useProxy && G_PROXY_BASE_URL) || '';

      this.ga4.initGA4(
        this.gaMeasurementId,
        baseUrl,
        {
          debug_mode: IS_DEBUG,
          isSgtmUrl: !!this.sgtmContainerUrl,
        },
        !this.customDomainStatus.isAvailable,
      );

      Logger.debug('GA4DestinationBundle: Initialized with measurement ID', {
        adBlockerDetected: !this.gtagStatus.isAvailable,
        gtagAvailable: this.gtagStatus.isAvailable,
        measurementId: this.gaMeasurementId,
        proxyAvailable: this.customDomainStatus.isAvailable,
        usingProxy: useProxy,
      });
    } catch (error) {
      Logger.error(
        'GA4DestinationBundle: Error initializing GA4 destination',
        error,
      );
    }
  }

  override async calculatePayload(
    mableEvent: TMableEvent,
  ): Promise<DestinationPayload> {
    const payload = transformMableEventToGA4Event(mableEvent);

    if (IS_DEBUG) {
      Logger.debug('GA4DestinationBundle: Calculated payload', {
        eventName: mableEvent.en,
        ga4EventName: payload.event_name,
      });
    }

    const googleUserData: TGtagUserData = await processGoogleUserData(
      mableEvent.cdas,
    );

    let modified_payload: Partial<DestinationPayload> = {
      event_name: payload.event_name,
      event_params: payload.event_params,
      user_data: googleUserData,
    };
    return modified_payload;
  }

  override async sendToDestination(
    payload: GA4Event,
  ): Promise<DestinationSendingResult> {
    if (payload.event_params.user_id) {
      this.ga4.setUserId(payload.event_params.user_id);
    }

    this.ga4.setUserData(payload.user_data);

    try {
      this.ga4.trackEvent(
        payload.event_name,
        this.gaMeasurementId,
        payload.event_params,
      );

      return {
        destinationReqEvent: payload,
        reason: 'Event successfully sent to GA4',
        success: true,
      };
    } catch (error) {
      return {
        destinationReqEvent: payload,
        reason:
          error instanceof Error ? error.message : 'Unknown error occurred',
        success: false,
      };
    }
  }
}
